import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material'
import Alert from '@mui/material/Alert'
import {
  Sync as SyncIcon,
  DeleteForever as WipeIcon,
  CloudDownload as FullSyncIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Stop as StopIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon
} from '@mui/icons-material'
import { useFetch } from '../../hooks'
import { PageLayout, PageHeader } from '../../components'
import { StatusChip } from '../../components/atoms'
import { authenticatedFetch, resolveAccessToken } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import styles from './SettingsFive9Sync.module.scss'

export default function SettingsFive9Sync () {
  const [syncing, setSyncing] = useState(false)
  const [logs, setLogs] = useState([])
  const [syncResult, setSyncResult] = useState(null)
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null })
  const [sessionId, setSessionId] = useState(null)
  const logsEndRef = useRef(null)
  const eventSourceRef = useRef(null)
  const streamEpochRef = useRef(0)

  const { data: stats, refetch: refetchStats } = useFetch(apiEndpoints.five9SyncStats, {
    transform: (data) => data.success ? data.data : null,
    errorMessage: 'Error fetching stats'
  })

  const { data: lastSync, refetch: refetchLastSync } = useFetch(apiEndpoints.five9SyncLastSync, {
    transform: (data) => data.success ? data.data : null,
    errorMessage: 'Error fetching last sync'
  })

  useEffect(() => {
    if (syncing && logs.length > 0) {
      logsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [logs, syncing])

  useEffect(() => {
    return () => {
      streamEpochRef.current += 1
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
    }
  }, [])

  const startSync = async (mode) => {
    setSyncing(true)
    setLogs([])
    setSyncResult(null)
    setConfirmDialog({ open: false, type: null })

    const newSessionId = `sync-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
    setSessionId(newSessionId)

    const apiKey = process.env.REACT_APP_API_KEY
    const streamEpoch = streamEpochRef.current
    const accessToken = await resolveAccessToken()
    if (streamEpoch !== streamEpochRef.current) {
      setSyncing(false)
      setSessionId(null)
      return
    }
    const url = `${apiEndpoints.five9SyncStart}?mode=${mode}&sessionId=${encodeURIComponent(newSessionId)}${apiKey ? `&apiKey=${encodeURIComponent(apiKey)}` : ''}${accessToken ? `&access_token=${encodeURIComponent(accessToken)}` : ''}`
    const eventSource = new EventSource(url)

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data)

      if (data.type === 'connected' && data.sessionId) {
        setSessionId(data.sessionId)
      } else if (data.type === 'log') {
        setLogs(prev => [...prev, data])
      } else if (data.type === 'complete' || data.type === 'cancelled') {
        setSyncResult(data)
        setSyncing(false)
        setSessionId(null)
        eventSource.close()
        refetchStats()
        refetchLastSync()
      } else if (data.type === 'error') {
        setSyncResult(data)
        setSyncing(false)
        setSessionId(null)
        eventSource.close()
      }
    }

    eventSource.onerror = (error) => {
      console.error('EventSource error:', error)
      setSyncing(false)
      setSessionId(null)
      eventSource.close()
      setLogs(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message: '❌ Connection error',
        error: 'Failed to maintain connection to server'
      }])
    }

    eventSourceRef.current = eventSource
  }

  const handleWipeAndSync = () => {
    setConfirmDialog({ open: true, type: 'wipe' })
  }

  const handleFullHistoricalSync = () => {
    setConfirmDialog({ open: true, type: 'full' })
  }

  const handleIncrementalSync = () => {
    startSync('incremental')
  }

  const handleConfirmSync = () => {
    if (confirmDialog.type === 'wipe') {
      startSync('wipe')
    } else if (confirmDialog.type === 'full') {
      startSync('full')
    }
  }

  const stopSync = async () => {
    /* istanbul ignore next -- stop button only renders when sessionId exists */
    if (!sessionId) {
      console.warn('No active session to stop')
      return
    }

    try {
      const response = await authenticatedFetch(apiEndpoints.five9SyncStop, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sessionId })
      })

      const data = await response.json()
      if (data.success) {
        setLogs(prev => [...prev, {
          timestamp: new Date().toISOString(),
          message: '🛑 Stop requested, waiting for current batch to complete...'
        }])
      } else {
        console.error('Failed to stop sync:', data.error)
        setSyncing(false)
        setSessionId(null)
      }
    } catch (error) {
      console.error('Error stopping sync:', error)
      setSyncing(false)
      setSessionId(null)
      setLogs(prev => [...prev, {
        timestamp: new Date().toISOString(),
        message: '⚠️  Error stopping sync, connection closed',
        error: error.message
      }])
    } finally {
      streamEpochRef.current += 1
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
    }
  }

  const formatNumber = (num) => {
    return num?.toLocaleString() || '0'
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleString()
  }

  const getLogColor = (message) => {
    if (message.includes('❌') || message.includes('Error')) return '#f44336'
    if (message.includes('✅') || message.includes('Success')) return '#81c784'
    if (message.includes('⚠️') || message.includes('Warning')) return '#ffb74d'
    if (message.includes('🔄') || message.includes('Starting')) return '#64b5f6'
    return 'rgba(255, 255, 255, 0.75)'
  }

  return (
    <PageLayout>
      <PageHeader
        title='Five9 Data Sync'
        subtitle='Manage data synchronization with Five9'
      />

      {/* Statistics */}
      {stats && (
        <Box className={styles.section}>
          <Box className={styles.sectionHeader}>
            <Typography variant='h6' className={styles.sectionTitle}>Database Statistics</Typography>
            <Button
              variant='outlined'
              size='small'
              onClick={refetchStats}
              disabled={syncing}
              startIcon={<RefreshIcon />}
              className={styles.syncButton}
            >
              Refresh
            </Button>
          </Box>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <Typography variant='subtitle1' className={styles.statCardTitle}>
                <StorageIcon fontSize='small' />
                Local Database
              </Typography>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Total Calls</span>
                <span className={styles.statValue}>{formatNumber(stats.local.totalCalls)}</span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Total Campaigns</span>
                <span className={styles.statValue}>{formatNumber(stats.local.totalCampaigns)}</span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Date Range</span>
                <span className={styles.statValue}>
                  {stats.local.earliestCall ? `${new Date(stats.local.earliestCall).toLocaleDateString()} - ${new Date(stats.local.latestCall).toLocaleDateString()}` : 'No data'}
                </span>
              </div>
              {lastSync && (
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Last Sync</span>
                  <span className={styles.statValue}>{formatDate(lastSync.lastSync)}</span>
                </div>
              )}
            </div>

            <div className={styles.statCard}>
              <Typography variant='subtitle1' className={styles.statCardTitle}>
                <CloudIcon fontSize='small' />
                Remote (Heroku)
              </Typography>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Total Calls</span>
                <span className={styles.statValue}>{formatNumber(stats.remote.totalCalls)}</span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Total Campaigns</span>
                <span className={styles.statValue}>{formatNumber(stats.remote.totalCampaigns)}</span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Date Range</span>
                <span className={styles.statValue}>
                  {stats.remote.earliestCall ? `${new Date(stats.remote.earliestCall).toLocaleDateString()} - ${new Date(stats.remote.latestCall).toLocaleDateString()}` : 'No data'}
                </span>
              </div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>Status</span>
                <StatusChip
                  label={stats.remote.totalCalls - stats.local.totalCalls > 0 ? `${formatNumber(stats.remote.totalCalls - stats.local.totalCalls)} to sync` : 'In sync'}
                  color={stats.remote.totalCalls - stats.local.totalCalls > 0 ? 'warning' : 'success'}
                  size='small'
                />
              </div>
            </div>
          </div>
        </Box>
      )}

      {/* Sync Options */}
      <Box className={styles.section}>
        <Typography variant='h6' className={styles.sectionTitle} gutterBottom>
          Sync Options
        </Typography>
        <div className={styles.syncCardsGrid}>
          <div className={styles.syncCard}>
            <div className={`${styles.syncCardIcon} ${styles.incremental}`}>
              <SyncIcon />
            </div>
            <Typography variant='subtitle1' className={styles.syncCardTitle}>
              Incremental Sync
            </Typography>
            <Typography variant='body2' className={styles.syncCardDescription}>
              Fetch calls from the last 7 days. Safe and fast.
            </Typography>
            <Button
              variant='contained'
              color='primary'
              fullWidth
              onClick={handleIncrementalSync}
              disabled={syncing}
              startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
              className={styles.syncButton}
            >
              {syncing ? 'Syncing...' : 'Start Incremental Sync'}
            </Button>
          </div>

          <div className={styles.syncCard}>
            <div className={`${styles.syncCardIcon} ${styles.full}`}>
              <FullSyncIcon />
            </div>
            <Typography variant='subtitle1' className={styles.syncCardTitle}>
              Full Historical Sync
            </Typography>
            <Typography variant='body2' className={styles.syncCardDescription}>
              Sync all historical data. Keeps existing calls, adds missing ones.
            </Typography>
            <Button
              variant='contained'
              color='primary'
              fullWidth
              onClick={handleFullHistoricalSync}
              disabled={syncing}
              startIcon={<FullSyncIcon />}
              className={styles.syncButton}
            >
              Full Historical Sync
            </Button>
          </div>

          <div className={styles.syncCard}>
            <div className={`${styles.syncCardIcon} ${styles.wipe}`}>
              <WipeIcon />
            </div>
            <Typography variant='subtitle1' className={styles.syncCardTitle}>
              Wipe & Full Sync
            </Typography>
            <Typography variant='body2' className={styles.syncCardDescription}>
              Delete all local data and fetch everything from remote. Destructive!
            </Typography>
            <Button
              variant='outlined'
              fullWidth
              onClick={handleWipeAndSync}
              disabled={syncing}
              startIcon={<WipeIcon />}
              className={`${styles.syncButton} ${styles.dangerButton}`}
            >
              Wipe & Sync
            </Button>
          </div>
        </div>
      </Box>

      {/* Sync Result */}
      {syncResult && (
        <Alert
          severity={syncResult.cancelled ? 'warning' : (syncResult.success ? 'success' : 'error')}
          icon={syncResult.cancelled ? <StopIcon /> : (syncResult.success ? <SuccessIcon /> : <ErrorIcon />)}
          className={styles.resultAlert}
        >
          {syncResult.cancelled ? (
            <>
              <strong>Sync cancelled by user</strong>
              <br />
              Mode: {syncResult.mode} |
              Processed: {syncResult.recordsProcessed} |
              Inserted: {syncResult.recordsInserted} |
              Updated: {syncResult.recordsUpdated} |
              Duration: {Math.round(syncResult.duration / 1000)}s
            </>
          ) : syncResult.success ? (
            <>
              <strong>Sync completed successfully!</strong>
              <br />
              Mode: {syncResult.mode} |
              Processed: {syncResult.recordsProcessed} |
              Inserted: {syncResult.recordsInserted} |
              Updated: {syncResult.recordsUpdated} |
              Duration: {Math.round(syncResult.duration / 1000)}s
            </>
          ) : (
            <>
              <strong>Sync failed:</strong> {syncResult.error}
            </>
          )}
        </Alert>
      )}

      {syncing && (
        <div className={styles.progressBar}>
          <LinearProgress style={{ flexGrow: 1 }} />
          <Button
            variant='outlined'
            color='secondary'
            size='small'
            onClick={stopSync}
            startIcon={<StopIcon />}
            className={styles.syncButton}
          >
            Stop Sync
          </Button>
        </div>
      )}

      {/* Logs */}
      <Box className={styles.section}>
        <Typography variant='h6' className={styles.sectionTitle} gutterBottom>
          Sync Logs
        </Typography>
        <div className={styles.logContainer}>
          {logs.length === 0 && !syncing && (
            <Typography variant='body2' className={styles.logEmpty}>
              Start a sync to see real-time progress.
            </Typography>
          )}
          {logs.map((log, index) => (
            <div key={index} className={styles.logEntry}>
              <span className={styles.logTimestamp}>
                {new Date(log.timestamp).toLocaleTimeString()}
              </span>
              <span className={styles.logMessage} style={{ color: getLogColor(log.message) }}>
                {log.message}
              </span>
              {log.count !== undefined && (
                <span className={styles.logCount}>
                  ({log.count} records)
                </span>
              )}
              {log.progress !== undefined && (
                <span className={styles.logProgress}>
                  {log.progress}%
                </span>
              )}
            </div>
          ))}
          <div ref={logsEndRef} />
        </div>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: null })}
      >
        <DialogTitle>
          {confirmDialog.type === 'wipe' ? 'Wipe Local Database?' : 'Full Historical Sync?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.type === 'wipe' ? (
              <>
                <strong>Warning:</strong> This will permanently delete all {formatNumber(stats?.local.totalCalls || 0)} calls
                from your local database, including any votes, comments, and custom data.
                <br /><br />
                Then it will fetch all {formatNumber(stats?.remote.totalCalls || 0)} calls from the remote server.
                <br /><br />
                This operation cannot be undone. Are you sure?
              </>
            ) : (
              <>
                This will fetch all historical data from the remote server ({formatNumber(stats?.remote.totalCalls || 0)} calls)
                and merge it with your local database.
                <br /><br />
                Existing calls will be updated, missing calls will be added. This may take several minutes.
                <br /><br />
                Continue?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, type: null })} color='primary'>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSync}
            color={confirmDialog.type === 'wipe' ? 'secondary' : 'primary'}
            variant='contained'
            autoFocus
          >
            {confirmDialog.type === 'wipe' ? 'Wipe & Sync' : 'Start Full Sync'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  )
}
